import React, { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import ClockIcon from 'transfix-icons/build/ClockIcon'
import { Typography, Box, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { ExistingBidCard } from 'apps/fleet-planner/components/bids'
import { useUserContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { OfferedShipment, useDeclineBid } from 'apps/fleet-planner/types'
import { Button } from 'transfix-ui'
import { formatPerMile, formatCurrency } from 'utils/formatCurrency'
import { DISPLAY } from 'constants/display'
import { InsightsTags } from './InsightsTags'

interface IBookBidSectionProps {
  onClickBid: () => void
  onClickContactUs: () => void
  onClickBookNow: () => void
  onDeclineHandler: (response: string) => void
  route?: string
  waterfallTimeLeftMinutes?: number
  waterfallOfferRate?: string | number
  isBookNowDisabled?: boolean
  shipmentOffer?: OfferedShipment
}

const useBookBidStyles = makeStyles(theme => ({
  offerText: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  rate: {
    paddingTop: theme.spacing(2),
  },
  waterfallText: {
    color: brandColors.info,
  },
}))

export const BookBidSection: FC<IBookBidSectionProps> = ({
  isBookNowDisabled,
  onClickBid: onClickBidProp,
  onClickBookNow,
  onClickContactUs,
  onDeclineHandler,
  route = 'book',
  shipmentOffer,
  waterfallOfferRate = '',
  waterfallTimeLeftMinutes = 0,
}) => {
  const isfmsDeclineBidEnabled = useFeatureFlag('fmsDeclineBid')
  const { shipmentId } = useParams<{ shipmentId: string }>()
  const { mutate: declineOffer } = useDeclineBid({ id: Number(shipmentId) })
  const [declineLoading, setDeclineLoading] = useState<boolean>(false)
  const { shipmentDetails } = useMarketplaceContext()
  const { userData } = useUserContext()
  const classes = useBookBidStyles()
  const { trackEvent } = useFMSAnalytics()
  const { id, carrierPayment, info, bids = [], status, stops = [], tags = [] } =
    shipmentDetails || {}
  const { appointment } = stops[0] || {}
  const { key: offerStatus } = status || {}
  const { motorCarriers = [] } = userData || {}
  const { carrierRate, targetCarrierRate: targetRate } = carrierPayment || {}
  const { totalMiles: miles } = info || {}
  const formattedMiles = parseFloat(miles)
  const filteredBids = bids.filter(({ status }) => status !== 'expired')
  // Sort bids where the newest bid is at the bottom
  const sortedBids = filteredBids.sort(
    (a, b) => new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime()
  )

  const isOffer = ['offered', 'countered'].includes(offerStatus)
  const isAutoOffer =
    ['waterfall_offered', 'tendered'].includes(offerStatus) && shipmentOffer?.externalId
  const isBiddable = ['tendered'].includes(offerStatus)
  const { isMobile } = useBreakpointContext()
  const canDeclineAcceptedOffers = isOffer && isfmsDeclineBidEnabled

  const declineHandler = async () => {
    setDeclineLoading(true)
    try {
      const { response = '' } = await declineOffer()
      trackEvent('Offers', 'CLICKED_DECLINE_IN_LOAD_DETAILS')
      setDeclineLoading(false)
      onDeclineHandler(response)
    } catch (e) {
      setDeclineLoading(false)
      onDeclineHandler('') // surface error message
    }
  }

  const onClickBid = () => {
    /**
     * In the case where a previous bid was declined
     * track that the user clicked Bid Again
     */
    const declinedBid = bids.find(b => b.status === 'declined')
    if (declinedBid !== undefined) {
      trackEvent('Bids Page', 'DECLINED_CLICKED_BID_AGAIN', { declinedBid })
    }
    onClickBidProp()
  }

  useEffect(() => {
    if (appointment?.appointmentType === 'FCFS') {
      trackEvent('Market Load Insights', 'FCFS_WINDOW_LABEL_SHOWN_ON_SHIPMENT_DETAILS')
    }

    if (tags.some(tag => tag.variant === 'popular_shipment')) {
      trackEvent('Market Load Insights', 'POPULAR_WINDOW_LABEL_SHOWN_ON_SHIPMENT_DETAILS')
    }

    if (tags.some(tag => tag.variant === 'great_rate_shipment')) {
      trackEvent('Market Load Insights', 'GREAT_WINDOW_LABEL_SHOWN_ON_SHIPMENT_DETAILS')
    }
  }, [appointment?.appointmentType, tags])

  return (
    <Box alignContent='center'>
      <Box paddingY={isMobile ? 0 : 2}>
        {/* Offered Rate Display */}
        {(isAutoOffer || isOffer) && (
          <>
            <Typography variant='h3' component='span' className={classes.rate}>
              {formatCurrency(waterfallOfferRate || carrierRate)}
            </Typography>
            {isAutoOffer && !!waterfallTimeLeftMinutes ? (
              <Box display='flex' className={classes.offerText}>
                <ClockIcon size='large' className={`col-xs-2 ${classes.waterfallText}`} />
                <Typography variant='subtitle2' className={`col-xs-10 ${classes.waterfallText}`}>
                  This offer expires in {waterfallTimeLeftMinutes} min
                </Typography>
              </Box>
            ) : (
              <Typography variant='subtitle2' color='textSecondary' className={classes.offerText}>
                Offered by Transfix Ops
              </Typography>
            )}
          </>
        )}
        {/* Target Rate Display */}
        {isBiddable && (
          <div>
            <Typography variant='h3' component='span' className={classes.rate}>
              {targetRate ? formatCurrency(targetRate) : DISPLAY.empty}
            </Typography>
            <Typography variant='subtitle2' color='textSecondary' component='span'>
              {' '}
              {miles && targetRate ? `$${formatPerMile(targetRate, formattedMiles, true)}/mi` : ''}
            </Typography>
          </div>
        )}
      </Box>

      <InsightsTags variant={appointment?.appointmentType} tags={tags} />

      {/* Existing Bids -> Bidding view only */}
      {isBiddable && sortedBids.length > 0 && (
        <div>
          {sortedBids.map(b => (
            <ExistingBidCard
              key={b.id}
              motorCarriers={motorCarriers}
              shipmentId={id}
              bid={b}
              route={route}
            />
          ))}
        </div>
      )}

      {/* Book Now Button */}
      <Box>
        <Button
          data-test='book-now-button'
          color='primary'
          fullWidth
          size='large'
          onClick={onClickBookNow}
          disabled={!id || isBookNowDisabled}>
          {isOffer ? 'Accept Offer' : 'Book Now'}
        </Button>
      </Box>
      {/* Decline Button -> Shipment offers only */}
      {(isAutoOffer || canDeclineAcceptedOffers) && (
        <Box paddingTop={2}>
          <Button
            fullWidth
            color='secondary'
            size='large'
            onClick={declineHandler}
            disabled={declineLoading}>
            Decline
          </Button>
        </Box>
      )}
      {/* Grey text -> Regular offers only */}
      {isOffer && (
        <Typography variant='subtitle2' color='textSecondary' align='center' className='pt3'>
          You’ll be asked to sign the rate confirmation in the next step.
        </Typography>
      )}
      {/* Submit a Bid button -> Marketplace loads only */}
      {isBiddable && (
        <Box className='mt4'>
          <Button
            data-test='submit-bid-cta'
            fullWidth
            color='secondary'
            size='large'
            onClick={onClickBid}
            disabled={!id}>
            Submit a New Bid
          </Button>
        </Box>
      )}
      {/* Contact Information */}
      <Box className='mt4'>
        <Button fullWidth color='tertiary' size='large' onClick={onClickContactUs} disabled={!id}>
          Contact Us
        </Button>
      </Box>
    </Box>
  )
}
